import { createHash } from 'node:crypto';
import type { PrismaClient, SalesDocument } from '@prisma/client';
import { z } from 'zod';
import { registerJobHandler, enqueueJob, backoffMs } from '../../core/jobs.js';

/**
 * EFRIS (Uganda Revenue Authority e-invoicing) adapter. PLAN.md section 13: an interface with an async
 * queue and retries, a status on the invoice, failures that never block invoicing, and OFF by default.
 *
 * Two adapters ship: `sandbox` (a deterministic stand-in so the whole flow can be tested and demonstrated
 * without URA credentials) and `live` (deliberately NOT implemented: it needs the company's URA device
 * registration, keys and the current URA API specification, which the owner and accountant must supply).
 */
export interface EfrisResult {
  fiscalNumber: string;
  verificationCode: string;
  qrData: string;
}
export interface EfrisDocument {
  docType: 'invoice' | 'creditnote';
  number: string;
  total: string;
  currency: string;
  issueDate: string;
  buyerName: string;
  buyerTin: string | null;
}
export interface EfrisAdapter {
  submitInvoice(doc: EfrisDocument): Promise<EfrisResult>;
  submitCreditNote(doc: EfrisDocument): Promise<EfrisResult>;
  getStatus(fiscalNumber: string): Promise<'accepted' | 'pending' | 'rejected'>;
}

export class EfrisNotConfigured extends Error {}

const fake = (doc: EfrisDocument): EfrisResult => {
  const hash = createHash('sha256')
    .update(`${doc.docType}|${doc.number}|${doc.total}`)
    .digest('hex');
  return {
    fiscalNumber: `SBX-${hash.slice(0, 10).toUpperCase()}`,
    verificationCode: hash.slice(10, 22).toUpperCase(),
    qrData: `https://efris.example/verify?fdn=${hash.slice(0, 10)}&sandbox=1`,
  };
};

export const sandboxAdapter: EfrisAdapter = {
  submitInvoice: async (d) => fake(d),
  submitCreditNote: async (d) => fake(d),
  getStatus: async () => 'accepted',
};

export const liveAdapter: EfrisAdapter = {
  submitInvoice: async () => {
    throw new EfrisNotConfigured(
      'Live EFRIS needs the URA device registration and keys. Set them up with your accountant first.',
    );
  },
  submitCreditNote: async () => {
    throw new EfrisNotConfigured(
      'Live EFRIS needs the URA device registration and keys. Set them up with your accountant first.',
    );
  },
  getStatus: async () => {
    throw new EfrisNotConfigured('Live EFRIS is not configured.');
  },
};

let override: EfrisAdapter | null = null;
/** Tests swap the adapter to prove retries, failures and idempotency. */
export const setEfrisAdapterForTests = (a: EfrisAdapter | null) => {
  override = a;
};
export const adapterFor = (mode: string): EfrisAdapter =>
  override ?? (mode === 'live' ? liveAdapter : sandboxAdapter);

export const efrisSettingsSchema = z.object({
  enabled: z.boolean(),
  mode: z.enum(['sandbox', 'live']).default('sandbox'),
});
export type EfrisSettings = z.infer<typeof efrisSettingsSchema>;

export async function getEfrisSettings(
  db: PrismaClient,
  companyId: number,
): Promise<EfrisSettings> {
  const row = await db.setting.findUnique({
    where: { companyId_key: { companyId, key: 'efris' } },
  });
  return {
    enabled: false,
    mode: 'sandbox',
    ...((row?.value as Partial<EfrisSettings> | undefined) ?? {}),
  };
}

export const MAX_ATTEMPTS = 6;

/** Queues submission after an invoice or credit note is approved. Never throws into the caller's flow. */
export async function scheduleEfris(
  db: PrismaClient,
  doc: Pick<SalesDocument, 'id' | 'companyId' | 'docType' | 'status'>,
): Promise<void> {
  try {
    if (
      !['invoice', 'creditnote'].includes(doc.docType) ||
      ['draft', 'pending_approval', 'void'].includes(doc.status)
    )
      return;
    const settings = await getEfrisSettings(db, doc.companyId);
    if (!settings.enabled) return;
    const company = await db.company.findUniqueOrThrow({ where: { id: doc.companyId } });
    if (!company.vatRegistered) return;
    const sub = await db.efrisSubmission.upsert({
      where: { docType_docId: { docType: doc.docType, docId: doc.id } },
      create: { companyId: doc.companyId, docType: doc.docType, docId: doc.id },
      update: {},
    });
    if (sub.status === 'submitted') return;
    await enqueueJob(db, {
      companyId: doc.companyId,
      type: 'efris.submit',
      payload: { submissionId: sub.id },
      idempotencyKey: `efris:${doc.docType}:${doc.id}`,
    });
  } catch {
    /* EFRIS problems must never block invoicing */
  }
}

/** Manual retry after a failure (or after switching from sandbox to live). */
export async function retryEfris(db: PrismaClient, docType: string, docId: number) {
  const sub = await db.efrisSubmission.findUnique({ where: { docType_docId: { docType, docId } } });
  if (!sub) return null;
  await db.efrisSubmission.update({
    where: { id: sub.id },
    data: { status: 'pending', attempts: 0, error: null },
  });
  await db.job.deleteMany({ where: { idempotencyKey: `efris:${docType}:${docId}` } });
  await enqueueJob(db, {
    companyId: sub.companyId,
    type: 'efris.submit',
    payload: { submissionId: sub.id },
    idempotencyKey: `efris:${docType}:${docId}`,
  });
  return sub.id;
}

export function registerEfrisJobs() {
  registerJobHandler('efris.submit', async (job, ctx) => {
    const { submissionId } = job.payload as { submissionId: number };
    const db = ctx.db;
    const sub = await db.efrisSubmission.findUnique({ where: { id: submissionId } });
    if (!sub || sub.status === 'submitted') return;
    const doc = await db.salesDocument.findUnique({ where: { id: sub.docId } });
    if (!doc || doc.status === 'void') {
      await db.efrisSubmission.update({
        where: { id: sub.id },
        data: { status: 'failed', error: 'The document is void or missing' },
      });
      return;
    }
    const settings = await getEfrisSettings(db, sub.companyId);
    if (!settings.enabled) return;
    const party = await db.party.findUnique({ where: { id: doc.partyId } });
    const attempts = sub.attempts + 1;
    try {
      const payload: EfrisDocument = {
        docType: sub.docType as 'invoice' | 'creditnote',
        number: doc.number ?? '',
        total: doc.total.toFixed(4),
        currency: doc.currency,
        issueDate: doc.issueDate.toISOString().slice(0, 10),
        buyerName: party?.name ?? '',
        buyerTin: party?.tin ?? null,
      };
      const adapter = adapterFor(settings.mode);
      const r =
        sub.docType === 'creditnote'
          ? await adapter.submitCreditNote(payload)
          : await adapter.submitInvoice(payload);
      await db.efrisSubmission.update({
        where: { id: sub.id },
        data: {
          status: 'submitted',
          attempts,
          fiscalNumber: r.fiscalNumber,
          verificationCode: r.verificationCode,
          qrData: r.qrData,
          error: null,
          submittedAt: ctx.now,
        },
      });
    } catch (e) {
      const message = (e instanceof Error ? e.message : 'Submission failed').slice(0, 480);
      const permanent = e instanceof EfrisNotConfigured || attempts >= MAX_ATTEMPTS;
      await db.efrisSubmission.update({
        where: { id: sub.id },
        data: { status: permanent ? 'failed' : 'pending', attempts, error: message },
      });
      if (permanent) {
        const owner = await db.user.findFirst({
          where: {
            companyId: sub.companyId,
            status: 'active',
            roles: { some: { role: { name: 'Owner' } } },
          },
          select: { id: true },
        });
        if (owner)
          await db.notification.create({
            data: {
              companyId: sub.companyId,
              userId: owner.id,
              title: `EFRIS submission for ${doc.number} failed: ${message}`,
              link: `/sales/invoices/${doc.id}`,
            },
          });
        return;
      }
      return { rescheduleAt: new Date(ctx.now.getTime() + backoffMs(attempts)) };
    }
  });
}
